import { useState } from "react";
import { CheckOutlined, DownOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import clsx from "clsx";
import { formatDate } from "@shared/utils";
import type { JournalVersion } from "../../../journal-version-list";
import "./version-switcher-dropdown.scss";

interface VersionSwitcherDropdownProps {
  versions: JournalVersion[];
  selectedVersionId?: string;
  currentVersionId?: string;
  onVersionSelect?: (version: JournalVersion) => void;
}

export const VersionSwitcherDropdown = ({
  versions,
  selectedVersionId: selectedVersionIdProp,
  currentVersionId,
  onVersionSelect,
}: VersionSwitcherDropdownProps) => {
  const latestVersion = versions[0];

  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    currentVersionId ?? latestVersion?.versionId ?? "",
  );

  const isControlled = selectedVersionIdProp !== undefined;
  const selectedId = isControlled ? selectedVersionIdProp : internalSelectedId;

  const selectedVersion =
    versions.find((v) => v.versionId === selectedId) ?? latestVersion;

  const handleSelect = (version: JournalVersion) => {
    if (!isControlled) {
      setInternalSelectedId(version.versionId);
    }
    onVersionSelect?.(version);
  };

  if (!versions.length || !selectedVersion) {
    return null;
  }

  const overlay = (
    <div className="version-switcher-dropdown__menu">
      {versions.map((version) => {
        const isLatest = version.versionId === latestVersion?.versionId;
        const isSelected = version.versionId === selectedId;

        return (
          <div
            key={version.versionId}
            className={clsx("version-switcher-dropdown__item", isLatest && "version-switcher-dropdown__item--latest", isSelected && "version-switcher-dropdown__item--selected")}
            onClick={() => handleSelect(version)}
          >
            <span className="version-switcher-dropdown__item-label">
              Версия {version.versionNumber} от{" "}
              {formatDate(version.versionDate)}
            </span>
            {isSelected && !isLatest && (
              <CheckOutlined className="version-switcher-dropdown__item-check" />
            )}
          </div>
        );
      })}
    </div>
  );

  const isSelectedActual = selectedVersion.status === "ACTUAL";
  const statusMod = isSelectedActual
    ? "version-switcher-dropdown__wrapper--actual"
    : "version-switcher-dropdown__wrapper--outdated";

  return (
    <Dropdown overlay={overlay} trigger={["click"]} placement="bottomLeft">
      <button
        type="button"
        className={clsx("version-switcher-dropdown__wrapper", statusMod)}
      >
        <span className="version-switcher-dropdown__inner">
          <span className="version-switcher-dropdown__inner-icon" aria-hidden>
            {isSelectedActual ? "⊙" : "⊘"}
          </span>
          <span className="version-switcher-dropdown__inner-label">
            Версия {selectedVersion.versionNumber}
          </span>
        </span>
        <span className="version-switcher-dropdown__arrow" aria-hidden>
          <DownOutlined />
        </span>
      </button>
    </Dropdown>
  );
};
